export type RcsVisualImagePreset = 'logo' | 'banner';

export interface RcsVisualImageSpec {
  width: number;
  height: number;
  maxBytes: number;
  cropRatio: string;
  mime: 'image/jpeg' | 'image/png';
}

export const RCS_VISUAL_IMAGE_PRESETS: Record<RcsVisualImagePreset, RcsVisualImageSpec> = {
  logo: {
    width: 224,
    height: 224,
    maxBytes: 50 * 1024,
    cropRatio: '1:1',
    mime: 'image/jpeg',
  },
  banner: {
    width: 1440,
    height: 448,
    maxBytes: 200 * 1024,
    cropRatio: '1440:448',
    mime: 'image/jpeg',
  },
};

const ALLOWED_IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|gif)$/i;
const ALLOWED_INPUT_MIME = /^image\//i;

/** Check whether a source can be opened before crop (URLs may lack a file extension). */
export const isAllowedImageSource = (file: File | string): boolean => {
  if (typeof file === 'string') {
    const path = file.split('?')[0].split('#')[0];
    if (ALLOWED_IMAGE_EXTENSIONS.test(path)) {
      return true;
    }
    return /^https?:\/\//i.test(file.trim());
  }
  if (file.type) {
    return ALLOWED_INPUT_MIME.test(file.type);
  }
  return ALLOWED_IMAGE_EXTENSIONS.test(file.name);
};

const MEDIA_CLIENTS_PREFIX = 'https://media.shopimind.io/clients/';

/** Build a public media URL from upload API responses. */
export const normalizeUploadedMediaUrl = (data: unknown): string | null => {
  if (!data) {
    return null;
  }
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) {
      return null;
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    return `${MEDIA_CLIENTS_PREFIX}${trimmed.replace(/^\//, '')}`;
  }
  if (Array.isArray(data) && data.length > 0) {
    const first = data[0] as { path?: string } | string;
    const path = typeof first === 'string' ? first : first?.path;
    if (path) {
      return `${MEDIA_CLIENTS_PREFIX}${String(path).replace(/^\//, '')}`;
    }
  }
  if (typeof data === 'object' && data !== null && 'path' in data) {
    const path = (data as { path: string }).path;
    if (path) {
      return `${MEDIA_CLIENTS_PREFIX}${String(path).replace(/^\//, '')}`;
    }
  }
  return null;
};

/** Preview URL (resize proxy), same pattern as FileManager thumbnails. */
export const getRcsVisualPreviewUrl = (
  url: string,
  preset: RcsVisualImagePreset,
): string => {
  const trimmed = url?.trim() || '';
  if (!trimmed) {
    return '';
  }
  if (!trimmed.includes('media.shopimind.io')) {
    return trimmed;
  }
  const { width, height } = RCS_VISUAL_IMAGE_PRESETS[preset];
  return `https://media.shopimind.io/resize/index.php?src=${trimmed}&w=${width}&h=${height}`;
};

/** Media API expects base64 data URLs (same as FileManager upload). */
export const blobToDataUrl = (blob: Blob): Promise<string> => new Promise(
  (resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('read_failed'));
    };
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(blob);
  },
);

type ImageSource = File | Blob | string;

const loadImageElement = (source: ImageSource): Promise<HTMLImageElement> => new Promise(
  (resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    let objectUrl: string | null = null;
    const cleanup = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };

    img.onload = () => {
      cleanup();
      resolve(img);
    };
    img.onerror = () => {
      cleanup();
      reject(new Error('image_load_failed'));
    };

    if (source instanceof Blob) {
      objectUrl = URL.createObjectURL(source);
      img.src = objectUrl;
      return;
    }

    img.src = source;
  },
);

export const exportImageToExactSize = async (
  source: ImageSource,
  width: number,
  height: number,
  mime = 'image/jpeg',
): Promise<Blob> => {
  const img = await loadImageElement(source);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas_context_failed');
  }

  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, mime, mime === 'image/jpeg' ? 0.92 : undefined);
  });

  if (!blob) {
    throw new Error('export_failed');
  }

  return blob;
};

export const compressBlobUnderMaxSize = async (
  blob: Blob,
  maxBytes: number,
  mime = 'image/jpeg',
): Promise<Blob> => {
  if (blob.size <= maxBytes) {
    return blob;
  }

  if (mime !== 'image/jpeg') {
    return blob;
  }

  const img = await loadImageElement(blob);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas_context_failed');
  }
  ctx.drawImage(img, 0, 0);

  const qualities = [0.9, 0.85, 0.8, 0.75, 0.7, 0.65, 0.6, 0.55, 0.5, 0.45, 0.4];
  let smallest: Blob = blob;

  for (let i = 0; i < qualities.length; i += 1) {
    const quality = qualities[i];
    // eslint-disable-next-line no-await-in-loop
    const candidate = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, mime, quality);
    });
    if (candidate) {
      if (candidate.size < smallest.size) {
        smallest = candidate;
      }
      if (candidate.size <= maxBytes) {
        return candidate;
      }
    }
  }

  if (smallest.size > maxBytes) {
    throw new Error('file_too_large');
  }

  return smallest;
};

export const processRcsVisualImageForUpload = async (
  source: ImageSource,
  preset: RcsVisualImagePreset,
): Promise<Blob> => {
  const spec = RCS_VISUAL_IMAGE_PRESETS[preset];

  let blob = await exportImageToExactSize(
    source,
    spec.width,
    spec.height,
    spec.mime,
  );
  blob = await compressBlobUnderMaxSize(blob, spec.maxBytes, spec.mime);
  return blob;
};

export const verifyImageDimensions = async (
  url: string,
  width: number,
  height: number,
): Promise<boolean> => {
  try {
    const img = await loadImageElement(url);
    return img.naturalWidth === width && img.naturalHeight === height;
  } catch {
    return false;
  }
};

const fetchImageByteSize = async (url: string): Promise<number | null> => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return blob.size;
  } catch {
    return null;
  }
};

/**
 * True when the library image already meets Vonage specs — crop/re-upload can be skipped.
 */
export const canUseRcsImageWithoutCrop = async (
  url: string,
  preset: RcsVisualImagePreset,
): Promise<boolean> => {
  const spec = RCS_VISUAL_IMAGE_PRESETS[preset];
  const dimensionsOk = await verifyImageDimensions(url, spec.width, spec.height);
  if (!dimensionsOk) {
    return false;
  }

  const byteSize = await fetchImageByteSize(url);
  if (byteSize === null) {
    return true;
  }

  return byteSize <= spec.maxBytes;
};
